import {
  renderFormattedDate,
  normalizeProjectCompanyCodes,
  getProjectCompanyCodeStatusCounts,
  createEmptyProjectCompanyCodeStatusCounts,
} from 'utils';
import {
  Box,
  Card,
  Chip,
  Stack,
  alpha,
  Button,
  Divider,
  useTheme,
  Typography,
  IconButton,
  CardContent,
  DialogTitle,
  DialogActions,
  DialogContent,
} from '@mui/material';
import { useMemo } from 'react';
import ProductCompanyStatusChip, {
  ProductCompanyStatusSummary,
  getProductCompanyStatusVisual,
} from 'components/product-company-status-chip';
import CloseIcon from '@mui/icons-material/Close';
import { PRODUCT_COMPANY_STATUS_TYPES } from 'enums';
import { FormattedMessage, useIntl } from 'react-intl';
import BusinessIcon from '@mui/icons-material/Business';
import ResponsiveDialog from 'components/responsive-dialog';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import type { BookCompany, BookProject, Project } from 'library/types';

type ShowProductsProps = {
  open: boolean;
  handleClose: () => void;
  clickedProducts: readonly BookProject[];
  allProducts: readonly Project[];
  companies: readonly BookCompany[];
  bookTitle?: string;
};

const ShowProducts = ({
  open,
  clickedProducts = [],
  allProducts = [],
  companies = [],
  bookTitle,
  handleClose,
}: ShowProductsProps) => {
  const intl = useIntl();
  const theme = useTheme();

  const companiesByCode = useMemo(() => new Map(companies.map((company) => [company.code, company])), [companies]);
  const statusCounts = useMemo(
    () =>
      clickedProducts.reduce((counts, product) => {
        const productCounts = getProjectCompanyCodeStatusCounts(product.companyCodes);

        PRODUCT_COMPANY_STATUS_TYPES.forEach((status) => {
          counts[status] += productCounts[status];
        });

        return counts;
      }, createEmptyProjectCompanyCodeStatusCounts()),
    [clickedProducts]
  );
  const companyCodeCount = PRODUCT_COMPANY_STATUS_TYPES.reduce((total, status) => total + statusCounts[status], 0);

  return (
    <ResponsiveDialog open={open} onClose={handleClose} maxWidth="md" aria-labelledby="product-details-title">
      <DialogTitle
        id="product-details-title"
        sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, pb: 1.5 }}>
        <Box display="flex" alignItems="center" gap={1.5} minWidth={0}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 36,
              height: 36,
              flexShrink: 0,
              borderRadius: 1.5,
              bgcolor: alpha(theme.palette.primary.main, 0.12),
              color: 'primary.main',
            }}>
            <AccountTreeIcon fontSize="small" />
          </Box>
          <Box minWidth={0}>
            <Typography variant="subtitle1" fontWeight={700} noWrap>
              <FormattedMessage id="report.projects" defaultMessage="Ürünler" />
            </Typography>
            {bookTitle && (
              <Typography variant="caption" color="text.secondary" noWrap display="block">
                {bookTitle}
              </Typography>
            )}
          </Box>
        </Box>
        <IconButton
          size="small"
          onClick={handleClose}
          aria-label={intl.formatMessage({ id: 'autocomplete.closeText', defaultMessage: 'Kapat' })}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center">
          <Chip
            label={intl.formatMessage(
              { id: 'report.productCount', defaultMessage: '{count} ürün' },
              { count: clickedProducts.length }
            )}
            color={clickedProducts.length > 0 ? 'primary' : 'default'}
            variant={clickedProducts.length > 0 ? 'filled' : 'outlined'}
            size="small"
            sx={{ fontWeight: 700 }}
          />
          <Chip
            label={intl.formatMessage(
              { id: 'report.companyCodeCount', defaultMessage: '{count} şirket kodu' },
              { count: companyCodeCount }
            )}
            variant="outlined"
            size="small"
            sx={{ fontWeight: 600 }}
          />
          <ProductCompanyStatusSummary counts={statusCounts} />
        </Stack>

        {clickedProducts.length === 0 ? (
          <Box py={5} textAlign="center">
            <AccountTreeIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
            <Typography color="text.secondary">
              <FormattedMessage id="report.noProducts" defaultMessage="Tanımlı ürün bulunmuyor." />
            </Typography>
          </Box>
        ) : (
          <Stack spacing={1.5}>
            {clickedProducts.map((bookProject, productIndex) => {
              const product = allProducts.find((item) => item.id === bookProject.id);
              const companyCodes = normalizeProjectCompanyCodes(bookProject.companyCodes);
              const productTitle =
                product?.title ??
                `${intl.formatMessage({ id: 'project.table.product', defaultMessage: 'Ürün' })} #${bookProject.id}`;

              return (
                <Card
                  key={`${bookProject.id}-${productIndex}`}
                  variant="outlined"
                  sx={{
                    borderRadius: 2,
                    transition: 'border-color 160ms ease, box-shadow 160ms ease',
                    '&:hover': {
                      borderColor: alpha(theme.palette.primary.main, 0.55),
                      boxShadow: theme.shadows[1],
                    },
                  }}>
                  <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                    <Stack
                      direction={{ xs: 'column', sm: 'row' }}
                      spacing={1}
                      alignItems={{ sm: 'center' }}
                      justifyContent="space-between">
                      <Box minWidth={0}>
                        <Typography variant="subtitle2" fontWeight={800} color="primary.main" noWrap>
                          {productTitle}
                        </Typography>
                        {product?.groupTitle && (
                          <Typography variant="caption" color="text.secondary" noWrap display="block">
                            {product.groupTitle}
                          </Typography>
                        )}
                      </Box>
                      <Chip
                        label={intl.formatMessage(
                          { id: 'report.companyCodeCount', defaultMessage: '{count} şirket kodu' },
                          { count: companyCodes.length }
                        )}
                        size="small"
                        variant="outlined"
                        sx={{ flexShrink: 0, fontWeight: 700 }}
                      />
                    </Stack>

                    <Divider sx={{ my: 1.5 }} />

                    {companyCodes.length === 0 ? (
                      <Typography variant="body2" color="text.secondary" textAlign="center" py={1}>
                        <FormattedMessage id="project.table.noCompanyCodes" defaultMessage="Tanımlı şirket kodu yok" />
                      </Typography>
                    ) : (
                      <Stack spacing={1}>
                        {companyCodes.map(({ companyCode, status }) => {
                          const company = companiesByCode.get(companyCode);
                          const statusVisual = getProductCompanyStatusVisual(status, theme);

                          return (
                            <Stack
                              key={companyCode}
                              direction={{ xs: 'column', sm: 'row' }}
                              spacing={1.25}
                              alignItems={{ sm: 'center' }}
                              justifyContent="space-between"
                              sx={{
                                px: 1.5,
                                py: 1.25,
                                border: '1px solid',
                                borderColor: statusVisual.borderColor,
                                borderRadius: 1.5,
                                bgcolor: statusVisual.backgroundColor,
                              }}>
                              <Stack direction="row" spacing={1.25} alignItems="center" minWidth={0}>
                                <BusinessIcon
                                  sx={{
                                    color: statusVisual.color,
                                    fontSize: 20,
                                    flexShrink: 0,
                                  }}
                                />
                                <Box minWidth={0}>
                                  <Stack direction="row" spacing={0.75} alignItems="center" useFlexGap flexWrap="wrap">
                                    <Typography variant="body2" fontWeight={700} noWrap>
                                      {company?.title || companyCode}
                                    </Typography>
                                    <Chip
                                      label={companyCode}
                                      size="small"
                                      variant="outlined"
                                      sx={{ height: 20, fontSize: '0.68rem', fontWeight: 700 }}
                                    />
                                  </Stack>
                                  <Stack direction="row" spacing={1.5} useFlexGap flexWrap="wrap">
                                    <Typography variant="caption" color="text.secondary">
                                      <strong>VKN:</strong> {company?.vkn || '-'}
                                    </Typography>
                                    {company?.dateOfAnnulment && (
                                      <Typography variant="caption" color="error.main">
                                        <FormattedMessage
                                          id="project.table.dateOfAnnulment"
                                          defaultMessage="Fesih Tarihi"
                                        />
                                        : {renderFormattedDate(company.dateOfAnnulment)}
                                      </Typography>
                                    )}
                                  </Stack>
                                </Box>
                              </Stack>
                              <ProductCompanyStatusChip
                                status={status}
                                sx={{ height: 22, flexShrink: 0, fontSize: '0.72rem', fontWeight: 700 }}
                              />
                            </Stack>
                          );
                        })}
                      </Stack>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button onClick={handleClose} variant="contained" size="small">
          <FormattedMessage id="dashboard.okButtonLabel" defaultMessage="Tamam" />
        </Button>
      </DialogActions>
    </ResponsiveDialog>
  );
};

export default ShowProducts;
